import { MAX_ITERATIONS, SMOOTHING_STEPS } from './fractalIteration';
import { PALETTE_SIZE, paletteTable } from './fractalPalette';

// GPU rendering shares the iteration budget and palette table with the CPU.
const VERTEX_SHADER_SRC = `
  precision highp float;
  attribute vec2 a_position;
  varying highp vec2 v_uv;
  void main() {
    v_uv = 0.5 * (a_position + 1.0);
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

export const COMPENSATED_ARITHMETIC_GLSL = `
  float addRounded(float a, float b) { return clamp(a + b, -u_arithmeticBound, u_arithmeticBound); }
  float subRounded(float a, float b) { return clamp(a - b, -u_arithmeticBound, u_arithmeticBound); }
  float mulRounded(float a, float b) { return clamp(a * b, -u_arithmeticBound, u_arithmeticBound); }

  vec2 dsAdd(vec2 a, vec2 b) {
    float s = addRounded(a.x, b.x);
    float v = subRounded(s, a.x);
    float e = addRounded(subRounded(a.x, subRounded(s, v)), subRounded(b.x, v));
    e = addRounded(addRounded(e, a.y), b.y);
    float hi = addRounded(s, e);
    return vec2(hi, subRounded(e, subRounded(hi, s)));
  }

  vec2 dsMul(vec2 a, vec2 b) {
    // Dekker split for a 24-bit significand (2^12 + 1).
    float ca = mulRounded(4097.0, a.x);
    float ah = subRounded(ca, subRounded(ca, a.x));
    float al = subRounded(a.x, ah);
    float cb = mulRounded(4097.0, b.x);
    float bh = subRounded(cb, subRounded(cb, b.x));
    float bl = subRounded(b.x, bh);
    float p = mulRounded(a.x, b.x);
    float e = addRounded(addRounded(addRounded(subRounded(mulRounded(ah, bh), p),
      mulRounded(ah, bl)), mulRounded(al, bh)), mulRounded(al, bl));
    e = addRounded(addRounded(e, mulRounded(a.x, b.y)), mulRounded(a.y, b.x));
    float hi = addRounded(p, e);
    return vec2(hi, subRounded(e, subRounded(hi, p)));
  }
`;

const FRAGMENT_SHADER_SRC = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform vec2 u_xRange;
  uniform vec2 u_yRange;
  uniform sampler2D u_palette;
  uniform float u_lagoon;
  uniform float u_gScale;
  uniform float u_phaseOffset;
  uniform float u_repeat;
  uniform float u_aScale;
  uniform float u_useMu;
  uniform float u_useFixedAlpha;
  uniform float u_aFixed;

  #ifdef DEEP_ZOOM
  invariant gl_FragColor;
  // A double-single number stores a leading float and its rounding residual.
  // Keep the compensated operations explicit: every intermediate is rounded
  // to highp precision. Coordinates AND the orbit need this extra precision.
  uniform vec2 u_centerX;
  uniform vec2 u_centerY;
  uniform vec2 u_spanX;
  uniform vec2 u_spanY;
  uniform vec2 u_resolution;
  // A runtime clamp preserves individual rounding points on fast-math drivers.
  // Its bound is far outside every value used by the bounded escape orbit.
  uniform float u_arithmeticBound;

  ${COMPENSATED_ARITHMETIC_GLSL}
  #endif

  void main() {
    #ifdef DEEP_ZOOM
    // Form a small offset before adding the split center. Interpolating the
    // absolute range endpoints would discard the low bits before iterating.
    vec2 offset = gl_FragCoord.xy / u_resolution - 0.5;
    vec2 cx = dsAdd(u_centerX, dsMul(u_spanX, vec2(offset.x, 0.0)));
    vec2 cy = dsAdd(u_centerY, dsMul(u_spanY, vec2(-offset.y, 0.0)));
    float x0 = cx.x + cx.y;
    float y0 = cy.x + cy.y;
    #else
    float x0 = mix(u_xRange.x, u_xRange.y, v_uv.x);
    // Flip Y to match the 2D canvas orientation (top row = yRange.x)
    float y0 = mix(u_yRange.y, u_yRange.x, v_uv.y);

    float q = (x0 - 0.25) * (x0 - 0.25) + y0 * y0;
    if (q * (q + x0 - 0.25) <= 0.25 * y0 * y0 ||
        (x0 + 1.0) * (x0 + 1.0) + y0 * y0 <= 0.0625) {
      gl_FragColor = vec4(0.0);
      return;
    }
    #endif

    // Mandelbrot: z² + c; start with x = y = 0
    float x = 0.0;
    float y = 0.0;
    float x2 = 0.0;
    float y2 = 0.0;
    float distSq = 0.0;
    float iterCount = 0.0;
    bool escaped = false;
    #if defined(STRIPE_AVERAGE) || defined(BINARY_CELLS) || defined(NORMAL_3D) || defined(PICKOVER_STALKS) || defined(CELLULAR_WEB) || defined(EQUIPOTENTIAL_RINGS)
    #define PATTERN_MODE 1
    #endif
    #ifdef STRIPE_AVERAGE
    float stripeSum = 0.0;
    float prevStripe = 0.0;
    float stripeCount = 0.0;
    const float BAILOUT = 10000.0;
    #elif defined(BINARY_CELLS)
    const float BAILOUT = 10000.0;
    #elif defined(NORMAL_3D)
    float dx = 0.0;
    float dy = 0.0;
    const float BAILOUT = 10000.0;
    #elif defined(PICKOVER_STALKS)
    float minStalk = 1e9;
    const float BAILOUT = 256.0;
    #elif defined(CELLULAR_WEB)
    float minRing = 1e9;
    const float BAILOUT = 256.0;
    #elif defined(EQUIPOTENTIAL_RINGS)
    const float BAILOUT = 256.0;
    #else
    const float BAILOUT = 4.0;
    #endif

    #ifdef DEEP_ZOOM
    vec2 zx = vec2(0.0);
    vec2 zy = vec2(0.0);
    vec2 zx2 = vec2(0.0);
    vec2 zy2 = vec2(0.0);
    for (int i = 0; i < ${MAX_ITERATIONS}; i++) {
      #ifdef NORMAL_3D
      float ox = zx.x + zx.y;
      float oy = zy.x + zy.y;
      float ndx = 2.0 * (ox * dx - oy * dy) + 1.0;
      float ndy = 2.0 * (ox * dy + oy * dx);
      dx = ndx;
      dy = ndy;
      #endif
      vec2 nextX = dsAdd(dsAdd(zx2, -zy2), cx);
      zy = dsAdd(2.0 * dsMul(zx, zy), cy);
      zx = nextX;
      zx2 = dsMul(zx, zx);
      zy2 = dsMul(zy, zy);
      iterCount += 1.0;
      #ifdef STRIPE_AVERAGE
      if (iterCount >= 2.0) {
        float sx = zx.x + zx.y;
        float sy = zy.x + zy.y;
        prevStripe = stripeSum;
        stripeSum += 0.5 * sin(6.0 * atan(sy, sx)) + 0.5;
        stripeCount += 1.0;
      }
      #elif defined(PICKOVER_STALKS)
      if (iterCount >= 2.0) {
        float sx = zx.x + zx.y;
        float sy = zy.x + zy.y;
        minStalk = min(minStalk, min(abs(sx), abs(sy)));
      }
      #elif defined(CELLULAR_WEB)
      if (iterCount >= 2.0) {
        float sx = zx.x + zx.y;
        float sy = zy.x + zy.y;
        minRing = min(minRing, abs(sqrt(sx * sx + sy * sy) - 1.25));
      }
      #endif
      // Check after advancing so the last permitted z_N is included.
      vec2 radius = dsAdd(zx2, zy2);
      if (radius.x > BAILOUT || (radius.x == BAILOUT && radius.y > 0.0)) {
        escaped = true;
        break;
      }
    }
    // Once escaped, ordinary floats suffice for the smooth color calculation.
    x = zx.x + zx.y;
    y = zy.x + zy.y;
    x2 = x * x;
    y2 = y * y;
    #else
    for (int i = 0; i < ${MAX_ITERATIONS}; i++) {
      #ifdef NORMAL_3D
      float ndx = 2.0 * (x * dx - y * dy) + 1.0;
      float ndy = 2.0 * (x * dy + y * dx);
      dx = ndx;
      dy = ndy;
      #endif
      float xnew = x2 - y2 + x0;
      float ynew = 2.0 * x * y + y0;
      x = xnew;
      y = ynew;
      x2 = x * x;
      y2 = y * y;
      iterCount += 1.0;
      #ifdef STRIPE_AVERAGE
      if (iterCount >= 2.0) {
        prevStripe = stripeSum;
        stripeSum += 0.5 * sin(6.0 * atan(y, x)) + 0.5;
        stripeCount += 1.0;
      }
      #elif defined(PICKOVER_STALKS)
      if (iterCount >= 2.0) {
        minStalk = min(minStalk, min(abs(x), abs(y)));
      }
      #elif defined(CELLULAR_WEB)
      if (iterCount >= 2.0) {
        minRing = min(minRing, abs(sqrt(x2 + y2) - 1.25));
      }
      #endif
      distSq = x2 + y2;
      if (distSq > BAILOUT) {
        escaped = true;
        break;
      }
    }
    #endif

    // If we never escaped within MAX_ITERATIONS, leave pixel transparent
    if (!escaped) {
      gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
      return;
    }

    #ifdef PATTERN_MODE
    float logZn = 0.5 * log(x2 + y2);
    float frac = 1.0 - log(logZn / (0.5 * log(BAILOUT))) / log(2.0);
    float mu = iterCount + frac;
    float shade = 1.0;
    #ifdef STRIPE_AVERAGE
    if (stripeCount >= 2.0) {
      float avg1 = stripeSum / stripeCount;
      float avg0 = prevStripe / (stripeCount - 1.0);
      float sa = avg0 + (avg1 - avg0) * clamp(frac, 0.0, 1.0);
      mu += 18.0 * sa;
      shade = 0.78 + 0.36 * sa;
    }
    #elif defined(BINARY_CELLS)
    float ang = atan(y, x);
    float cellX = sin(ang * 4.0);
    float cellY = sin(frac * 6.283185307179586);
    float grid = cellX * cellY;
    float edge = min(abs(cellX), abs(cellY));
    float border = pow(max(1e-6, clamp(edge / 0.18, 0.0, 1.0)), 0.45);
    mu += grid > 0.0 ? 6.0 : 0.0;
    shade = border * (grid > 0.0 ? 1.08 : 0.78);
    #elif defined(NORMAL_3D)
    float dMag2 = dx * dx + dy * dy;
    if (dMag2 > 0.0) {
      float ux = (x * dx + y * dy) / dMag2;
      float uy = (y * dx - x * dy) / dMag2;
      float uMag = max(1e-20, sqrt(ux * ux + uy * uy));
      float nx = ux / uMag;
      float ny = uy / uMag;
      float dotVal = (nx * -0.7071 + ny * -0.7071 + 1.35) / sqrt(1.0 + 1.35 * 1.35);
      shade = clamp(0.25 + 0.95 * max(0.0, dotVal), 0.18, 1.35);
    }
    #elif defined(PICKOVER_STALKS)
    float stalk = exp(-minStalk * 28.0);
    mu += stalk * 24.0;
    shade = 0.72 + 0.55 * stalk;
    #elif defined(CELLULAR_WEB)
    float web = exp(-minRing * 22.0);
    mu += web * 20.0;
    shade = 0.70 + 0.55 * web;
    #elif defined(EQUIPOTENTIAL_RINGS)
    float wave = 0.5 + 0.5 * cos(mu * 3.141592653589793 * 1.5);
    float ray = 0.5 + 0.5 * cos(atan(y, x) * 8.0);
    float net = max(pow(max(1e-6, wave), 6.0), pow(max(1e-6, ray), 8.0));
    mu += net * 10.0;
    shade = 0.72 + 0.48 * net;
    #endif
    #else
    // Evaluate the continuous count farther along the escaped orbit to remove
    // the residual seams caused by smoothing at radius 2 alone.
    for (int i = 0; i < ${SMOOTHING_STEPS}; i++) {
      float xnew = x2 - y2 + x0;
      y = 2.0 * x * y + y0;
      x = xnew;
      x2 = x * x;
      y2 = y * y;
    }
    float logDist = 0.5 * log(x2 + y2);
    float mu = iterCount + ${SMOOTHING_STEPS + 1}.0 - log(logDist) / log(2.0);
    #endif

    float val = u_useMu > 0.5 ? mu : iterCount;
    float position = val * u_gScale / 255.0;
    float t = u_repeat > 0.5
      ? 0.5 - 0.5 * cos(3.141592653589793 * position)
      : clamp(position, 0.0, 1.0);
    float a = u_useFixedAlpha > 0.5 ? u_aFixed : clamp(val * u_aScale, 0.0, 255.0);
    vec3 color;
    if (u_lagoon > 0.5) {
      float phase = (sqrt(max(0.0, val) + 1.0) - 1.0) * u_gScale / 55.0 + u_phaseOffset;
      // Half a texel places integral table positions at sample centers.
      color = texture2D(u_palette, vec2(fract(phase) + 0.5 / ${PALETTE_SIZE}.0, 0.5)).rgb;
    } else {
      color = mix(vec3(0.0, 0.0, 1.0), vec3(1.0, 1.0, 0.0), t);
    }
    #ifdef PATTERN_MODE
    if (u_useMu > 0.5) {
      color = min(vec3(1.0), color * shade);
    }
    #endif
    gl_FragColor = vec4(color, a / 255.0);
  }
`;

export function splitFloat(value) {
  const high = Math.fround(value);
  return [high, Math.fround(value - high)];
}

// Switch before coordinate rounding becomes visible. Keep extra headroom for
// accumulated orbit error, and use hysteresis so small zoom changes don't
// alternate shaders. The arithmetic floor accounts for orbits near |z| = 1.
export function needsDeepZoom(xRange, yRange, width, height, wasDeep = false, precision = 23) {
  const unitsPerPixel = Math.min(
    Math.abs(xRange[1] - xRange[0]) / width,
    Math.abs(yRange[1] - yRange[0]) / height
  );
  const magnitude = Math.max(1, ...xRange.map(Math.abs), ...yRange.map(Math.abs));
  const roundingStep = 2 ** (Math.floor(Math.log2(magnitude)) - precision);
  return unitsPerPixel <= roundingStep * (wasDeep ? 32 : 16);
}

function compileShader(gl, type, src, deferValidation = false) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!deferValidation && !gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile: ${log}`);
  }
  return shader;
}

export function createWebGLProgram(gl, vsSrc, fsSrc, deferValidation = false) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc, deferValidation);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc, deferValidation);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!deferValidation && !gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link: ${log}`);
  }
  return program;
}

function deepProgram(gl) {
  return createWebGLProgram(gl, VERTEX_SHADER_SRC, '#define DEEP_ZOOM\n' + FRAGMENT_SHADER_SRC, true);
}

/**
 * Try to get a WebGL context. Prefer WebGL2, fall back to WebGL1.
 * Returns { gl, isWebGL2 } or null if not supported.
 */
export function getWebGLContext(canvas) {
  if (!canvas) return null;
  let gl = typeof WebGL2RenderingContext !== 'undefined'
    ? canvas.getContext('webgl2', { alpha: false, antialias: false }) : null;
  const isWebGL2 = !!gl;
  if (!gl) {
    gl = canvas.getContext('webgl', { alpha: false, antialias: false });
  }
  if (!gl) return null;
  return { gl, isWebGL2 };
}

const programBindings = new WeakMap();
const QUAD_VERTICES = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
const UNIFORM_NAMES = [
  'u_xRange', 'u_yRange', 'u_gScale', 'u_aScale', 'u_useMu',
  'u_centerX', 'u_centerY', 'u_spanX', 'u_spanY', 'u_resolution', 'u_arithmeticBound',
  'u_phaseOffset', 'u_repeat', 'u_useFixedAlpha', 'u_aFixed', 'u_palette', 'u_lagoon'
];

function getProgramBindings(gl, program) {
  let bindings = programBindings.get(program);
  if (!bindings) {
    bindings = { a_position: gl.getAttribLocation(program, 'a_position') };
    for (const name of UNIFORM_NAMES) bindings[name] = gl.getUniformLocation(program, name);
    programBindings.set(program, bindings);
  }
  return bindings;
}

// The cached image uses framebuffer coordinates (bottom-left origin). During a
// drag, snap the camera to this pixel grid; the gesture's final draw is exact.
export function planWebGLPan(previous, xRange, yRange, width, height) {
  if (!previous || previous.width !== width || previous.height !== height) return null;
  const sx = (previous.xRange[1] - previous.xRange[0]) / width;
  const sy = (previous.yRange[1] - previous.yRange[0]) / height;
  if (!Number.isFinite(sx) || !Number.isFinite(sy) || sx === 0 || sy === 0) return null;
  // Express the tolerance in pixels, not absolute world coordinates: an
  // absolute epsilon can accidentally accept a zoom as a pan at deep scales.
  if (Math.abs((xRange[1] - xRange[0]) / sx - width) > 1e-4 ||
      Math.abs((yRange[1] - yRange[0]) / sy - height) > 1e-4) return null;
  const dx = Math.round((xRange[0] - previous.xRange[0]) / sx);
  const dy = Math.round((yRange[0] - previous.yRange[0]) / sy);
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.abs(dx) >= width || Math.abs(dy) >= height) return null;
  const strips = [];
  if (dx) strips.push([dx > 0 ? width - dx : 0, 0, Math.abs(dx), height]);
  // Exclude the corner already covered by the vertical strip.
  if (dy) strips.push([dx < 0 ? -dx : 0, dy > 0 ? 0 : height + dy,
    width - Math.abs(dx), Math.abs(dy)]);
  return { dx, dy, strips,
    xRange: previous.xRange.map(x => x + dx * sx),
    yRange: previous.yRange.map(y => y + dy * sy),
    shadedPixels: strips.reduce((sum, [, , w, h]) => sum + w * h, 0) };
}

function smallCachedPan(canvas, xRange, yRange, paletteKey, deepZoom) {
  const panCache = canvas._fractalPanCache;
  if (panCache?.failed) return null;
  const zoomCache = canvas._fractalZoomCache;
  for (const [frame, fromZoom] of [
    [panCache?.texture && panCache.frame, false],
    [!zoomCache?.failed && zoomCache?.sourceTexture && zoomCache.frame, true],
  ]) {
    if (!frame || frame.exact === false || frame.paletteKey !== paletteKey || frame.deepZoom !== deepZoom) continue;
    const plan = planWebGLPan(frame, xRange, yRange, canvas.width, canvas.height);
    if (plan && plan.shadedPixels <= canvas.width * canvas.height / 16) return { plan, frame, fromZoom };
  }
  return null;
}

/** Admit small native-grid pans without submitting a full fractal shader. */
export function canRenderWebGLPan(canvas, xRange, yRange, colorParams) {
  if (colorParams?.mode === 'buddhabrot' || colorParams?.mode === 'nebulabrot') return false;
  const ctx = getWebGLContext(canvas);
  if (!ctx || ctx.gl.isContextLost?.() || canvas.width <= 0 || canvas.height <= 0) return false;
  const precision = canvas._fractalFloatPrecision ??
    ctx.gl.getShaderPrecisionFormat(ctx.gl.FRAGMENT_SHADER, ctx.gl.HIGH_FLOAT)?.precision ?? 23;
  const deepZoom = needsDeepZoom(xRange, yRange, canvas.width, canvas.height,
    canvas._fractalDeepZoom, precision);
  return !!smallCachedPan(canvas, xRange, yRange, JSON.stringify(colorParams || {}), deepZoom);
}

const COPY_FRAGMENT_SHADER = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform sampler2D u_image;
  uniform vec2 u_offset;
  void main() { gl_FragColor = texture2D(u_image, v_uv + u_offset); }
`;

const ZOOM_FRAGMENT_SHADER = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform sampler2D u_source;
  uniform sampler2D u_detail;
  // Source texture coordinates for the requested camera's bottom-left UV.
  uniform vec4 u_projection;
  uniform float u_blend;
  void main() {
    if (u_blend >= 1.0) {
      gl_FragColor = texture2D(u_detail, v_uv);
      return;
    }
    vec2 sourceUV = v_uv * u_projection.xz + u_projection.yw;
    bool covered = all(greaterThanEqual(sourceUV, vec2(0.0))) &&
      all(lessThanEqual(sourceUV, vec2(1.0)));
    vec4 oldColor = covered ? texture2D(u_source, sourceUV)
      : vec4(10.0 / 255.0, 14.0 / 255.0, 24.0 / 255.0, 1.0);
    if (u_blend <= 0.0) {
      gl_FragColor = oldColor;
      return;
    }
    vec4 detailColor = texture2D(u_detail, v_uv);
    gl_FragColor = mix(oldColor, detailColor, u_blend);
  }
`;

// Map the requested viewport's bottom-left texture coordinates back to the
// last exact image. The Y offset differs from X because yRange is top-first.
export function planWebGLZoom(previous, xRange, yRange, width, height) {
  if (!previous || previous.width !== width || previous.height !== height) return null;
  const oldX = previous.xRange[1] - previous.xRange[0];
  const oldY = previous.yRange[1] - previous.yRange[0];
  const newX = xRange[1] - xRange[0];
  const newY = yRange[1] - yRange[0];
  if (![oldX, oldY, newX, newY].every(value => Number.isFinite(value) && value > 0)) return null;
  const projection = [newX / oldX, (xRange[0] - previous.xRange[0]) / oldX,
    newY / oldY, (previous.yRange[1] - yRange[1]) / oldY];
  return projection.every(Number.isFinite) ? projection : null;
}

function bindQuad(gl, canvas, position) {
  let buffer = canvas._fractalWebGLBuffer;
  if (!buffer) {
    buffer = gl.createBuffer();
    canvas._fractalWebGLBuffer = buffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTICES, gl.STATIC_DRAW);
  } else gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
}

function copyPanFrame(gl, canvas, cache, pan) {
  if (!cache.program) {
    cache.program = createWebGLProgram(gl, VERTEX_SHADER_SRC, COPY_FRAGMENT_SHADER);
    cache.position = gl.getAttribLocation(cache.program, 'a_position');
    cache.image = gl.getUniformLocation(cache.program, 'u_image');
    cache.offset = gl.getUniformLocation(cache.program, 'u_offset');
  }
  gl.useProgram(cache.program);
  gl.disable(gl.BLEND);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.texture);
  gl.uniform1i(cache.image, 0);
  gl.uniform2f(cache.offset, pan.dx / canvas.width, -pan.dy / canvas.height);
  bindQuad(gl, canvas, cache.position);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

function savePanFrame(gl, canvas, frame) {
  let cache = canvas._fractalPanCache;
  if (!cache) cache = canvas._fractalPanCache = { texture: gl.createTexture() };
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.texture);
  if (cache.width !== frame.width || cache.height !== frame.height) {
    // The drawing buffer is opaque (alpha:false). WebGL 1 requires a compatible
    // RGB destination for copying it; sampling RGB supplies an alpha of one.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, frame.width, frame.height, 0, gl.RGB, gl.UNSIGNED_BYTE, null);
    cache.width = frame.width;
    cache.height = frame.height;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
  // Capture before the browser discards the default drawing buffer. This is a
  // GPU-to-GPU copy; there is no readPixels/readback or preserveDrawingBuffer.
  gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, frame.width, frame.height);
  cache.frame = frame;
}

export function disposeWebGLPanCache(canvas) {
  const cache = canvas?._fractalPanCache;
  if (!cache) return;
  const gl = getWebGLContext(canvas)?.gl;
  if (gl) {
    gl.deleteTexture(cache.texture);
    if (cache.program) gl.deleteProgram(cache.program);
  }
  delete canvas._fractalPanCache;
}

export const MAX_ACTIVE_WEBGL_CONTEXTS = 6;
const activeWebGLCanvases = new Map();

function isCanvasNearViewport(canvas) {
  if (typeof window === 'undefined' || !canvas?.isConnected
    || typeof canvas.getBoundingClientRect !== 'function') return false;
  const rect = canvas.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement?.clientHeight || 800;
  const vw = window.innerWidth || document.documentElement?.clientWidth || 1200;
  const right = rect.right ?? ((rect.left ?? 0) + (rect.width ?? 0));
  const bottom = rect.bottom ?? ((rect.top ?? 0) + (rect.height ?? 0));
  return (rect.width ?? 0) > 0 && (rect.height ?? 0) > 0
    && bottom >= -200 && (rect.top ?? 0) <= vh + 200
    && right >= -200 && (rect.left ?? 0) <= vw + 200;
}

const PATTERN_MODE_DEFINES = {
  stripe: 'STRIPE_AVERAGE',
  binary_cells: 'BINARY_CELLS',
  normal_3d: 'NORMAL_3D',
  pickover_stalks: 'PICKOVER_STALKS',
  cellular_web: 'CELLULAR_WEB',
  equipotential_rings: 'EQUIPOTENTIAL_RINGS',
};

export function disposeWebGLResources(canvasEl) {
  if (!canvasEl) return;
  activeWebGLCanvases.delete(canvasEl);
  const gl = canvasEl._fractalContext?.gl || canvasEl._fractalZoomCache?.gl;
  disposeWebGLPanCache(canvasEl);
  disposeWebGLZoomCache(canvasEl);
  try {
    if (gl && !gl.isContextLost?.()) {
      if (canvasEl._fractalWebGLBuffer) gl.deleteBuffer?.(canvasEl._fractalWebGLBuffer);
      if (canvasEl._fractalPaletteTexture) gl.deleteTexture?.(canvasEl._fractalPaletteTexture);
      if (canvasEl._fractalWebGLProgram) gl.deleteProgram?.(canvasEl._fractalWebGLProgram);
      if (canvasEl._fractalDeepWebGLProgram) gl.deleteProgram?.(canvasEl._fractalDeepWebGLProgram);
      if (canvasEl._fractalStripeWebGLProgram) gl.deleteProgram?.(canvasEl._fractalStripeWebGLProgram);
      if (canvasEl._fractalDeepStripeWebGLProgram) gl.deleteProgram?.(canvasEl._fractalDeepStripeWebGLProgram);
      if (canvasEl._fractalPatternPrograms) {
        for (const prog of Object.values(canvasEl._fractalPatternPrograms)) {
          gl.deleteProgram?.(prog);
        }
      }
      if (canvasEl._fractalPendingDeepProgram) gl.deleteProgram?.(canvasEl._fractalPendingDeepProgram);
    }
  } catch { /* Ignore teardown errors on a lost or disposed context. */ }
  delete canvasEl._fractalWebGLBuffer;
  delete canvasEl._fractalPaletteTexture;
  delete canvasEl._fractalWebGLProgram;
  delete canvasEl._fractalDeepWebGLProgram;
  delete canvasEl._fractalStripeWebGLProgram;
  delete canvasEl._fractalDeepStripeWebGLProgram;
  delete canvasEl._fractalPatternPrograms;
  delete canvasEl._fractalPendingDeepProgram;
}

export function offloadWebGLCanvas(canvasEl) {
  if (!canvasEl) return false;
  const entry = activeWebGLCanvases.get(canvasEl);
  activeWebGLCanvases.delete(canvasEl);
  const gl = entry?.gl || canvasEl._fractalContext?.gl || canvasEl._fractalZoomCache?.gl;
  if (typeof document !== 'undefined' && canvasEl.isConnected
    && canvasEl.width > 0 && canvasEl.height > 0 && !gl?.isContextLost?.()) {
    try {
      const cache = canvasEl._fractalZoomCache;
      if (cache?.sourceTexture && cache.frame) {
        const prepared = prepareZoomCache(gl, canvasEl, canvasEl.width, canvasEl.height, { program: true });
        if (prepared?.program) {
          gl.disable(gl.SCISSOR_TEST);
          gl.disable(gl.DEPTH_TEST);
          presentZoomFrame(gl, canvasEl, prepared, [1, 0, 1, 0]);
        }
      }
      if (typeof HTMLCanvasElement === 'undefined' || canvasEl instanceof HTMLCanvasElement) {
        const snapshot = document.createElement('canvas');
        snapshot.width = canvasEl.width;
        snapshot.height = canvasEl.height;
        const ctx2d = snapshot.getContext('2d');
        if (ctx2d) {
          ctx2d.drawImage(canvasEl, 0, 0);
          if (canvasEl._offloadedSnapshot && canvasEl._offloadedSnapshot !== snapshot) {
            canvasEl._offloadedSnapshot.width = 0;
            canvasEl._offloadedSnapshot.height = 0;
          }
          canvasEl._offloadedSnapshot = snapshot;
        }
      }
    } catch { /* Ignore snapshot copy errors. */ }
  }
  disposeWebGLResources(canvasEl);
  if (!gl || gl.isContextLost?.()) return false;
  try {
    const loseExt = gl.getExtension?.('WEBGL_lose_context');
    if (loseExt?.loseContext) {
      canvasEl._webGLOffloaded = true;
      loseExt.loseContext();
      return true;
    }
  } catch { /* Ignore extension errors. */ }
  return false;
}

function trackActiveWebGLCanvas(canvasEl, gl, xRange, yRange, colorParams) {
  if (!canvasEl || (!canvasEl.isConnected && typeof gl?.getExtension !== 'function')) return;
  activeWebGLCanvases.delete(canvasEl);
  activeWebGLCanvases.set(canvasEl, {
    gl,
    xRange: Array.isArray(xRange) ? [xRange[0], xRange[1]] : null,
    yRange: Array.isArray(yRange) ? [yRange[0], yRange[1]] : null,
    colorParams,
  });
  if (activeWebGLCanvases.size <= MAX_ACTIVE_WEBGL_CONTEXTS) return;
  for (const [candidate, record] of activeWebGLCanvases) {
    if (candidate === canvasEl) continue;
    if (candidate.isConnected === false || record.gl?.isContextLost?.()) {
      disposeWebGLResources(candidate);
      continue;
    }
    if (!isCanvasNearViewport(candidate)) {
      offloadWebGLCanvas(candidate);
      if (activeWebGLCanvases.size <= MAX_ACTIVE_WEBGL_CONTEXTS) return;
    }
  }
  while (activeWebGLCanvases.size > MAX_ACTIVE_WEBGL_CONTEXTS) {
    const [oldest] = activeWebGLCanvases.keys();
    if (!oldest || oldest === canvasEl) break;
    if (!offloadWebGLCanvas(oldest)) {
      activeWebGLCanvases.delete(oldest);
      break;
    }
  }
}

function releaseZoomResources(gl, cache) {
  if (cache.sourceTexture) gl.deleteTexture(cache.sourceTexture);
  if (cache.detailTexture) gl.deleteTexture(cache.detailTexture);
  if (cache.framebuffer) gl.deleteFramebuffer(cache.framebuffer);
  if (cache.program) gl.deleteProgram(cache.program);
  cache.sourceTexture = null;
  cache.detailTexture = null;
  cache.framebuffer = null;
  cache.program = null;
  cache.frame = null;
  cache.previousFrame = null;
  cache.transition = null;
  cache.interactive = false;
  cache.panPreview = false;
}

export function disposeWebGLZoomCache(canvas) {
  const cache = canvas?._fractalZoomCache;
  if (!cache) return;
  releaseZoomResources(cache.gl, cache);
  delete canvas._fractalZoomCache;
  delete canvas._fractalSourceProjection;
}

// Allocate the source when an exact visible frame is drawn. The composite
// program and second full-size target are deferred until somebody zooms.
function prepareZoomCache(gl, canvas, width, height, { program = false, detail = false, history = false } = {}) {
  let cache = canvas._fractalZoomCache;
  if (cache?.failed) return null;
  if (!cache) cache = canvas._fractalZoomCache = { gl };
  try {
    if (!cache.sourceTexture) {
      cache.sourceTexture = gl.createTexture();
      if (!cache.sourceTexture) throw new Error('Zoom source unavailable');
    }
    if (program && !cache.program) {
      cache.program = createWebGLProgram(gl, VERTEX_SHADER_SRC, ZOOM_FRAGMENT_SHADER);
      cache.position = gl.getAttribLocation(cache.program, 'a_position');
      cache.source = gl.getUniformLocation(cache.program, 'u_source');
      cache.detail = gl.getUniformLocation(cache.program, 'u_detail');
      cache.projection = gl.getUniformLocation(cache.program, 'u_projection');
      cache.blend = gl.getUniformLocation(cache.program, 'u_blend');
    }
    const resized = cache.width !== width || cache.height !== height;
    if (resized) {
      cache.width = width;
      cache.height = height;
      cache.frame = null;
      cache.previousFrame = null;
      cache.transition = null;
      cache.interactive = false;
      cache.panPreview = false;
    }
    const newDetail = (detail || history) && !cache.detailTexture;
    if (newDetail) {
      cache.detailTexture = gl.createTexture();
      if (!cache.detailTexture) throw new Error('Zoom target unavailable');
    }
    if (detail && !cache.framebuffer) {
      cache.framebuffer = gl.createFramebuffer();
      if (!cache.framebuffer) throw new Error('Zoom target unavailable');
    }
    for (const texture of [resized && cache.sourceTexture,
      cache.detailTexture && (resized || newDetail) && cache.detailTexture].filter(Boolean)) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      // RGB is renderable in WebGL1 and matches the opaque drawing buffer.
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, width, height,
        0, gl.RGB, gl.UNSIGNED_BYTE, null);
    }
    if (detail) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, cache.framebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, cache.detailTexture, 0);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error('Zoom target incomplete');
      }
    }
    return cache;
  } catch (error) {
    // Caching is optional: resource pressure must not disable exact WebGL draws.
    releaseZoomResources(gl, cache);
    cache.failed = true;
    return null;
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}

function presentZoomFrame(gl, canvas, cache, projection, blend = 0) {
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.useProgram(cache.program);
  gl.disable(gl.BLEND);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.sourceTexture);
  gl.uniform1i(cache.source, 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, cache.transition ? cache.detailTexture : cache.sourceTexture);
  gl.uniform1i(cache.detail, 1);
  gl.uniform4f(cache.projection, ...projection);
  gl.uniform1f(cache.blend, blend);
  bindQuad(gl, canvas, cache.position);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

function captureZoomFrame(gl, canvas, frame, retainPrevious = false) {
  const previous = canvas._fractalZoomCache?.frame;
  const changedCamera = previous && (previous.xRange[0] !== frame.xRange[0] ||
    previous.xRange[1] !== frame.xRange[1] || previous.yRange[0] !== frame.yRange[0] ||
    previous.yRange[1] !== frame.yRange[1]);
  const keepPrevious = retainPrevious && changedCamera && previous.exact !== false &&
    previous.width === frame.width && previous.height === frame.height &&
    previous.paletteKey === frame.paletteKey;
  const cache = prepareZoomCache(gl, canvas, frame.width, frame.height, { history: !!keepPrevious });
  if (!cache) return;
  if (keepPrevious) {
    // Keep the original exact bitmap for history. The new visible image is
    // copied straight from the current-camera draw, never from a composite.
    [cache.sourceTexture, cache.detailTexture] = [cache.detailTexture, cache.sourceTexture];
    cache.previousFrame = previous;
  } else if (!retainPrevious || changedCamera || previous?.paletteKey !== frame.paletteKey ||
      previous?.exact === false) cache.previousFrame = null;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.sourceTexture);
  gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, frame.width, frame.height);
  cache.frame = frame;
  cache.transition = null;
  cache.interactive = false;
  cache.panPreview = false;
}

/** Prepare a private single-grid assembly in optional history storage. */
export function prepareWebGLProgressiveFrame(canvas, frame) {
  const ctx = getWebGLContext(canvas);
  if (!ctx || ctx.gl.isContextLost?.() || frame.width !== canvas.width || frame.height !== canvas.height) return null;
  const cache = prepareZoomCache(ctx.gl, canvas, canvas.width, canvas.height, { history: true });
  if (!cache?.detailTexture) return null;
  // Optional prior history is evicted before reusing its storage. The retained
  // original source stays visible until the entire new grid has been copied.
  cache.previousFrame = null;
  return { cache, texture: cache.detailTexture, frame, width: canvas.width, height: canvas.height };
}

/** The caller binds its tile framebuffer; each copy belongs to its GPU slice. */
export function copyWebGLProgressiveTile(canvas, assembly, sourceX, sourceY, x, y, width, height) {
  const gl = assembly?.cache?.gl;
  if (!gl || canvas._fractalZoomCache !== assembly.cache || canvas.width !== assembly.width ||
      canvas.height !== assembly.height) return false;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, assembly.texture);
  gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, x, y, sourceX, sourceY, width, height);
  return true;
}

/** Promote only after every copy has retired for this immutable grid. */
export function commitWebGLProgressiveFrame(canvas, assembly) {
  const cache = assembly?.cache;
  if (!cache || canvas._fractalZoomCache !== cache || assembly.texture !== cache.detailTexture ||
      canvas.width !== assembly.width || canvas.height !== assembly.height) return false;
  const prior = cache.frame;
  [cache.sourceTexture, cache.detailTexture] = [cache.detailTexture, cache.sourceTexture];
  cache.previousFrame = prior?.exact !== false && prior?.paletteKey === assembly.frame.paletteKey ? prior : null;
  cache.frame = { ...assembly.frame, exact: true };
  cache.transition = null; cache.interactive = false; cache.panPreview = false;
  return true;
}

/** Present one blend frame. At progress 1, promote the new exact texture. */
export function presentWebGLZoomTransition(canvas, progress) {
  const cache = canvas?._fractalZoomCache;
  if (!cache?.transition || cache.gl.isContextLost?.()) return false;
  if (cache.transition.frame.width !== canvas.width || cache.transition.frame.height !== canvas.height) {
    discardWebGLZoomTransition(canvas);
    return false;
  }
  const amount = Math.min(1, Math.max(0, progress));
  presentZoomFrame(cache.gl, canvas, cache, cache.transition.projection, amount);
  cache.transition.displayedBlend = amount;
  if (amount === 1) {
    const oldFrame = cache.frame;
    [cache.sourceTexture, cache.detailTexture] = [cache.detailTexture, cache.sourceTexture];
    cache.frame = cache.transition.frame;
    cache.previousFrame = oldFrame?.exact === false ? null : oldFrame;
    cache.transition = null;
    cache.interactive = false;
  }
  return true;
}

/** Retain the visible fade at its target camera before another gesture starts. */
export function retainWebGLZoomTransition(canvas) {
  const cache = canvas?._fractalZoomCache;
  const transition = cache?.transition;
  if (!transition || cache.gl.isContextLost?.() ||
      transition.frame.width !== canvas.width || transition.frame.height !== canvas.height) return false;
  try {
    const gl = cache.gl;
    // The default drawing buffer may have been discarded after presentation.
    // Recreate its last blend from the two retained textures before copying.
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.DEPTH_TEST);
    presentZoomFrame(gl, canvas, cache, transition.projection, transition.displayedBlend ?? 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, cache.sourceTexture);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, canvas.width, canvas.height);
    cache.frame = { ...transition.frame, exact: false };
    cache.previousFrame = null;
    cache.transition = null;
    cache.interactive = true;
    return true;
  } catch (error) {
    // The next exact render remains available if texture retention fails.
    return false;
  }
}

/** Drop pending detail without changing the retained exact source image. */
export function discardWebGLZoomTransition(canvas) {
  const cache = canvas?._fractalZoomCache;
  if (!cache?.transition) return false;
  cache.transition = null;
  cache.interactive = false;
  return true;
}

/** Restore the cached exact bitmap for CSS transforms during deep handoff. */
export function presentWebGLZoomSource(canvas) {
  let cache = canvas?._fractalZoomCache;
  if (!cache?.frame || cache.gl.isContextLost?.()) return false;
  if (!cache.transition && !cache.interactive && !cache.panPreview) return false;
  if (cache.transition) presentWebGLZoomTransition(canvas, 1);
  else {
    cache = prepareZoomCache(cache.gl, canvas, canvas.width, canvas.height, { program: true });
    if (!cache?.frame) return false;
    presentZoomFrame(cache.gl, canvas, cache, [1, 0, 1, 0]);
  }
  cache.interactive = false;
  cache.panPreview = false;
  return true;
}

/** Project the original exact grid without an inverse CSS transform or a copy. */
export function presentWebGLOriginalSource(canvas, transform, metadata = {}) {
  let cache = canvas?._fractalZoomCache;
  const source = cache?.frame;
  const { scale, offset } = transform || {};
  if (!source || source.exact === false || !cache.sourceTexture || cache.gl.isContextLost?.() ||
      source.width !== canvas.width || source.height !== canvas.height ||
      metadata.paletteKey !== undefined && metadata.paletteKey !== source.paletteKey ||
      scale?.length !== 2 || offset?.length !== 2 ||
      ![...scale, ...offset].every(value => Number.isFinite(value) && Number.isFinite(Math.fround(value))) ||
      scale.some(value => value < 0)) return false;
  try {
    cache = prepareZoomCache(cache.gl, canvas, canvas.width, canvas.height, { program: true });
    if (!cache?.sourceTexture || cache.frame !== source) return false;
    const gl = cache.gl;
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.DEPTH_TEST);
    // Decimal projection uses top-left UVs; the retained texture is bottom-first.
    // A scale underflowing to zero truthfully samples one original source point.
    const projection = [scale[0], offset[0], scale[1], 1 - offset[1] - scale[1]];
    if (!projection.every(value => Number.isFinite(Math.fround(value)))) return false;
    presentZoomFrame(gl, canvas, cache, projection);
    cache.transition = null;
    cache.interactive = true;
    cache.panPreview = false;
    const axisBounds = (amount, start) => amount === 0
      ? start >= 0 && start <= 1 ? [0, 1] : [0, 0]
      : [Math.max(0, Math.min(1, -start / amount)), Math.max(0, Math.min(1, (1 - start) / amount))];
    const [left, right] = axisBounds(scale[0], offset[0]);
    const [top, bottom] = axisBounds(scale[1], offset[1]);
    const sampleFootprint = [canvas.width / (source.width * scale[0]),
      canvas.height / (source.height * scale[1])];
    canvas._fractalSourceProjection = Object.freeze({ ...metadata,
      scale: Object.freeze([...scale]), offset: Object.freeze([...offset]),
      sourceGrid: Object.freeze({ x: 0, y: 0, width: source.width, height: source.height,
        frameWidth: source.width, frameHeight: source.height }),
      cameraRequestedAt: source.cameraRequestedAt ?? null,
      sourceCameraRequestedAt: source.cameraRequestedAt ?? null,
      paletteKey: source.paletteKey, bounds: Object.freeze([left, top, right, bottom]),
      sourceCoverage: Math.max(0, right - left) * Math.max(0, bottom - top),
      submissionId: cache.sourceProjectionSequence = (cache.sourceProjectionSequence || 0) + 1,
      sampleFootprint: Object.freeze(sampleFootprint),
      sampleFootprintLimited: sampleFootprint.some(value => !Number.isFinite(value)),
      exactCurrentCamera: false, fullCoverage: left === 0 && top === 0 && right === 1 && bottom === 1 });
    // The default framebuffer now contains only this named original source.
    canvas._fractalProgressiveTiles = [];
    canvas._fractalRenderStats = { reused: true, reprojected: true, originalSourceProjection: true,
      shadedPixels: 0, totalPixels: canvas.width * canvas.height, exact: false,
      sourceProjection: canvas._fractalSourceProjection };
    return true;
  } catch (error) { return false; }
}

/**
 * Draw the Mandelbrot set with WebGL.
 * colorParams: { r, gScale, b, aScale, aFixed, useMu } (same as worker).
 * options.cachePan retains a GPU image; options.pan reuses compatible pixels
 * at a snapped viewport. options.interactiveZoom reprojects the last exact
 * full-resolution frame while the gesture is active. options.interactiveExact
 * computes exact pixels during a gesture and blends them from the projected
 * source before promoting the exact frame. options.zoomTransition:false draws
 * the exact current camera directly, including during a gesture, without any
 * projected-image preview or detail fade.
 * Omit options for an exact final/undo draw.
 * Returns true if drawn, false if WebGL failed (caller should fall back to canvas).
 */
export function drawMandelbrotFractalWebGL(canvasEl, xRange, yRange, colorParams, options = {}) {
  if (colorParams?.mode === 'buddhabrot' || colorParams?.mode === 'nebulabrot') return false;
  const requestedXRange = xRange;
  const requestedYRange = yRange;
  const ctx = getWebGLContext(canvasEl);
  if (!ctx) return false;

  const { gl } = ctx;
  if (gl.isContextLost?.()) return false;
  const w = canvasEl.width;
  const h = canvasEl.height;
  const directZoom = options.zoomTransition === false;
  const offscreenTarget = options.offscreenTarget;
  if (!offscreenTarget) delete canvasEl._fractalSourceProjection;

  if (w <= 0 || h <= 0) return false;

  if (canvasEl._fractalFloatPrecision === undefined) {
    canvasEl._fractalFloatPrecision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)?.precision ?? 23;
  }
  const deepZoom = needsDeepZoom(xRange, yRange, w, h,
    canvasEl._fractalDeepZoom, canvasEl._fractalFloatPrecision);
  const paletteKey = JSON.stringify(colorParams || {});
  const cached = canvasEl._fractalPanCache?.frame;
  const panRequested = !offscreenTarget && !options.interactiveZoom && !options.interactiveExact &&
    options.cachePan && options.pan && !canvasEl._fractalPanCache?.failed;
  let pan = panRequested && canvasEl._fractalPanCache?.texture &&
    cached?.deepZoom === deepZoom && cached.paletteKey === paletteKey
    ? planWebGLPan(cached, xRange, yRange, w, h) : null;
  if (panRequested && (!pan || pan.shadedPixels > w * h / 16)) {
    const seed = smallCachedPan(canvasEl, xRange, yRange, paletteKey, deepZoom);
    if (seed?.fromZoom) {
      try {
        const cache = prepareZoomCache(gl, canvasEl, w, h, { program: true });
        if (!cache?.sourceTexture || cache.frame !== seed.frame) return false;
        gl.disable(gl.SCISSOR_TEST);
        gl.disable(gl.DEPTH_TEST);
        // Seed from the named exact source, never from a mixed visible preview.
        presentZoomFrame(gl, canvasEl, cache, [1, 0, 1, 0]);
        savePanFrame(gl, canvasEl, { ...seed.frame,
          xRange: [...seed.frame.xRange], yRange: [...seed.frame.yRange] });
        pan = seed.plan;
      } catch (error) { return false; }
    }
  }
  if (pan) {
    xRange = pan.xRange;
    yRange = pan.yRange;
  }
  // Start compiling shortly before the switch. With parallel compilation,
  // the current shader keeps rendering while the driver prepares this one.
  if (!options.projectionOnly && !deepZoom && !canvasEl._fractalDeepWebGLProgram &&
      !canvasEl._fractalPendingDeepProgram &&
      needsDeepZoom(xRange, yRange, w * 4, h * 4, false, canvasEl._fractalFloatPrecision)) {
    if (gl.getExtension('KHR_parallel_shader_compile')) {
      canvasEl._fractalPendingDeepProgram = deepProgram(gl);
    }
  }
  const zoomCache = offscreenTarget ? null : canvasEl._fractalZoomCache;
  if (zoomCache?.transition) {
    if (directZoom) discardWebGLZoomTransition(canvasEl);
    else presentWebGLZoomTransition(canvasEl, 1);
  }
  const source = zoomCache?.frame;
  const projection = source?.paletteKey === paletteKey
    ? planWebGLZoom(source, xRange, yRange, w, h) : null;
  const prior = zoomCache?.previousFrame;
  // Direct zooms retain exact history textures but defer the copy shader until
  // an undo/redo actually requests those pixels.
  if (options.twoFrameCache && prior && !zoomCache.program) {
    prepareZoomCache(gl, canvasEl, w, h, { program: true });
  }
  const replay = options.twoFrameCache && !deepZoom && !options.interactiveZoom &&
    !options.interactiveExact && !options.cachePan && zoomCache?.program &&
    zoomCache.sourceTexture && zoomCache.detailTexture && prior &&
    !zoomCache.interactive && !zoomCache.panPreview && !zoomCache.transition &&
    source?.exact !== false && prior.exact !== false &&
    source.deepZoom === false && prior.deepZoom === false &&
    source.paletteKey === paletteKey && prior.paletteKey === paletteKey &&
    source.width === w && source.height === h && prior.width === w && prior.height === h &&
    prior.xRange[0] === xRange[0] && prior.xRange[1] === xRange[1] &&
    prior.yRange[0] === yRange[0] && prior.yRange[1] === yRange[1];
  if (replay) {
    const oldFrame = zoomCache.frame;
    const replayTexture = zoomCache.detailTexture;
    [zoomCache.sourceTexture, zoomCache.detailTexture] =
      [zoomCache.detailTexture, zoomCache.sourceTexture];
    zoomCache.frame = prior;
    zoomCache.previousFrame = oldFrame;
    let presented = false;
    let restoreFailed = false;
    try {
      gl.disable(gl.SCISSOR_TEST);
      gl.disable(gl.DEPTH_TEST);
      // Exact 1:1 history pixels must sample their original texel centers.
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, replayTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      presentZoomFrame(gl, canvasEl, zoomCache, [1, 0, 1, 0]);
      if (gl.getError?.() !== gl.NO_ERROR) throw new Error('WebGL history replay failed');
      presented = true;
    } catch (error) {
      presented = false;
    } finally {
      try {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, replayTexture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        if (gl.getError?.() !== gl.NO_ERROR) restoreFailed = true;
      } catch (error) { restoreFailed = true; }
    }
    if (restoreFailed) {
      // A texture with an unknown filter must never be used by a later fade.
      releaseZoomResources(gl, zoomCache);
      zoomCache.failed = true;
      presented = false;
    } else if (!presented) {
      [zoomCache.sourceTexture, zoomCache.detailTexture] =
        [zoomCache.detailTexture, zoomCache.sourceTexture];
      zoomCache.frame = oldFrame;
      zoomCache.previousFrame = null;
    }
    if (presented) {
      canvasEl._fractalDeepZoom = false;
      if (canvasEl._fractalPanCache) canvasEl._fractalPanCache.frame = null;
      zoomCache.replayHits = (zoomCache.replayHits || 0) + 1;
      canvasEl._fractalRenderStats = { reused: true, twoFrameReplay: true,
        shadedPixels: 0, totalPixels: w * h };
      return true;
    }
  }
  const activeZoomCache = !directZoom && options.interactiveZoom && !options.interactiveExact && projection
    ? prepareZoomCache(gl, canvasEl, w, h, { program: true }) : null;
  if (activeZoomCache && projection) {
    canvasEl._fractalDeepZoom = deepZoom;
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.DEPTH_TEST);
    presentZoomFrame(gl, canvasEl, activeZoomCache, projection);
    activeZoomCache.interactive = true;
    // Projection changes only the visible buffer. Keep the untouched pan
    // texture; its palette, grid and arithmetic checks still govern reuse.
    canvasEl._fractalRenderStats = { reused: true, reprojected: true, interactiveZoom: true,
      shadedPixels: 0, totalPixels: w * h };
    return true;
  }
  // Retirement presentation must never fall through to an unbounded shader.
  if (options.projectionOnly) return false;
  if (zoomCache && !directZoom) zoomCache.previousFrame = null;
  const cp = colorParams || {};
  const patternDefine = PATTERN_MODE_DEFINES[cp.mode] || null;
  const programKey = patternDefine
    ? `_fractalPattern_${patternDefine}_${deepZoom ? 'deep' : 'shallow'}`
    : (deepZoom ? '_fractalDeepWebGLProgram' : '_fractalWebGLProgram');
  let program = patternDefine ? canvasEl._fractalPatternPrograms?.[programKey] : canvasEl[programKey];
  if (!program) {
    try {
      if (deepZoom && !patternDefine && canvasEl._fractalPendingDeepProgram) {
        program = canvasEl._fractalPendingDeepProgram;
        canvasEl._fractalPendingDeepProgram = null;
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
          const log = gl.getProgramInfoLog(program);
          gl.deleteProgram(program);
          throw new Error(`Deep zoom shader link: ${log}`);
        }
      } else {
        const prefix = `${deepZoom ? '#define DEEP_ZOOM\n' : ''}${patternDefine ? `#define ${patternDefine}\n` : ''}`;
        program = createWebGLProgram(gl, VERTEX_SHADER_SRC,
          prefix + FRAGMENT_SHADER_SRC);
      }
      if (patternDefine) {
        if (!canvasEl._fractalPatternPrograms) canvasEl._fractalPatternPrograms = {};
        canvasEl._fractalPatternPrograms[programKey] = program;
      } else {
        canvasEl[programKey] = program;
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('WebGL program creation failed:', err.message);
      }
      return false;
    }
  }
  canvasEl._fractalDeepZoom = deepZoom;

  const gScale = cp.gScale !== undefined ? cp.gScale : 7;
  const aScale = cp.aScale !== undefined ? cp.aScale : 15;
  const useMu = cp.useMu !== undefined ? cp.useMu : true;

  const sameSourceCamera = source && source.xRange[0] === xRange[0] && source.xRange[1] === xRange[1] &&
    source.yRange[0] === yRange[0] && source.yRange[1] === yRange[1];
  const transitionCache = !directZoom && !options.interactiveZoom && !options.cachePan && projection &&
    (options.interactiveExact && !sameSourceCamera || !options.interactiveExact && zoomCache?.interactive)
    ? prepareZoomCache(gl, canvasEl, w, h, { program: true, detail: true }) : null;
  if (offscreenTarget) gl.bindFramebuffer(gl.FRAMEBUFFER, offscreenTarget);
  else if (transitionCache) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, transitionCache.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D,
      transitionCache.detailTexture, 0);
  } else gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, w, h);
  gl.disable(gl.SCISSOR_TEST);
  gl.disable(gl.DEPTH_TEST);
  if (pan) {
    try {
      copyPanFrame(gl, canvasEl, canvasEl._fractalPanCache, pan);
    } catch (error) {
      // A copy-shader failure must not take down the working fractal renderer.
      canvasEl._fractalPanCache.failed = true;
      pan = null;
      xRange = requestedXRange;
      yRange = requestedYRange;
    }
  }
  gl.useProgram(program);

  // The opaque WebGL drawing buffer must use the same background as CSS/2D.
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(10 / 255, 14 / 255, 24 / 255, 1);
  if (!pan) gl.clear(gl.COLOR_BUFFER_BIT);

  const bindings = getProgramBindings(gl, program);
  const { u_xRange, u_yRange, u_gScale, u_aScale, u_useMu } = bindings;

  gl.uniform2f(u_xRange, xRange[0], xRange[1]);
  gl.uniform2f(u_yRange, yRange[0], yRange[1]);
  if (deepZoom) {
    const spanX = xRange[1] - xRange[0];
    const spanY = yRange[1] - yRange[0];
    gl.uniform2f(bindings.u_centerX, ...splitFloat(xRange[0] + spanX / 2));
    gl.uniform2f(bindings.u_centerY, ...splitFloat(yRange[0] + spanY / 2));
    gl.uniform2f(bindings.u_spanX, ...splitFloat(spanX));
    gl.uniform2f(bindings.u_spanY, ...splitFloat(spanY));
    gl.uniform2f(bindings.u_resolution, w, h);
    gl.uniform1f(bindings.u_arithmeticBound, 1e30);
  }
  gl.uniform1f(u_gScale, gScale);
  gl.uniform1f(bindings.u_phaseOffset, cp.phaseOffset ?? 0);
  gl.uniform1f(bindings.u_repeat, cp.repeat ? 1 : 0);
  const useFixedAlpha = cp.aFixed != null && cp.aFixed !== undefined;
  gl.uniform1f(u_aScale, aScale);
  gl.uniform1f(u_useMu, useMu ? 1 : 0);
  gl.uniform1f(bindings.u_useFixedAlpha, useFixedAlpha ? 1 : 0);
  gl.uniform1f(bindings.u_aFixed, useFixedAlpha ? cp.aFixed : 0);

  gl.activeTexture(gl.TEXTURE0);
  let texture = canvasEl._fractalPaletteTexture;
  if (!texture) {
    texture = gl.createTexture();
    canvasEl._fractalPaletteTexture = texture;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, PALETTE_SIZE, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, paletteTable);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, texture);
  }
  gl.uniform1i(bindings.u_palette, 0);
  gl.uniform1f(bindings.u_lagoon, cp.style === 'lagoon' ? 1 : 0);

  bindQuad(gl, canvasEl, bindings.a_position);
  if (pan) {
    gl.enable(gl.SCISSOR_TEST);
    for (const strip of pan.strips) {
      gl.scissor(...strip);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    gl.disable(gl.SCISSOR_TEST);
  } else gl.drawArrays(gl.TRIANGLES, 0, 6);
  // Bounded direct work renders into private history storage. Its caller
  // fences this draw and publishes it at the next mandatory presentation.
  if (offscreenTarget) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return true;
  }
  const exactFrame = {
    xRange: [...xRange], yRange: [...yRange], width: w, height: h, deepZoom, paletteKey
  };
  if (transitionCache) {
    transitionCache.transition = { frame: exactFrame, projection,
      interactiveExact: !!options.interactiveExact };
    transitionCache.interactive = false;
    presentZoomFrame(gl, canvasEl, transitionCache, projection);
  } else if (options.cachePan && options.pan) {
    if (canvasEl._fractalZoomCache) canvasEl._fractalZoomCache.panPreview = true;
  } else captureZoomFrame(gl, canvasEl, exactFrame, directZoom);
  if (!options.interactiveZoom && options.cachePan && !canvasEl._fractalPanCache?.failed) savePanFrame(gl, canvasEl, {
    xRange: [...xRange], yRange: [...yRange], width: w, height: h, deepZoom, paletteKey
  });
  else if (canvasEl._fractalPanCache) canvasEl._fractalPanCache.frame = null;
  // Exposed for the benchmark: counts fractal evaluations, excluding the cheap
  // texture presentation and copy, whose cost is included in measured time.
  canvasEl._fractalRenderStats = { reused: !!pan, shadedPixels: pan ? pan.shadedPixels : w * h,
    totalPixels: w * h };
  if (options.interactiveZoom) canvasEl._fractalRenderStats.interactiveZoom = true;
  if (options.interactiveExact) canvasEl._fractalRenderStats.interactiveExact = true;
  if (transitionCache) canvasEl._fractalRenderStats.transitionZoom = true;
  trackActiveWebGLCanvas(canvasEl, gl, xRange, yRange, colorParams);
  return true;
}
